import {
  TYPE_USAGE_ARRAY,
  TYPE_USAGE_GENERIC,
  TYPE_USAGE_GENERIC_ALIAS,
  TYPE_USAGE_PRIMITIVE,
} from '../../../const/graftModelPayload.const'
import { UnifiedGraftModelTypeKey } from '../../../const/unifiedGraftModelTypeKeys.const'

import { getEffectiveTypeUsageKind, hasPayload, isNullable, nullable, resolveNode } from './nodeHelpers'
import type { NodeLike } from './types'

function formatInner(x: unknown): string {
  return hasPayload(x) ? formatTypeUsage(x) : 'unknown'
}

function formatNode(node: NodeLike): string {
  const kind = getEffectiveTypeUsageKind(node)
  const p = node.payload as unknown[]

  switch (kind) {
    case UnifiedGraftModelTypeKey.TYPE_USAGE_VOID:
      return 'void' + nullable(p)
    case UnifiedGraftModelTypeKey.TYPE_USAGE_PRIMITIVE: {
      const ns = p[TYPE_USAGE_PRIMITIVE.NAMESPACE] as string
      const name = p[TYPE_USAGE_PRIMITIVE.TYPENAME] as string
      return (ns ? `${ns}.${name}` : name) + nullable(p)
    }
    case UnifiedGraftModelTypeKey.TYPE_USAGE_GENERIC_ALIAS:
      return (p[TYPE_USAGE_GENERIC_ALIAS.ALIAS_NAME] as string) + nullable(p)
    case UnifiedGraftModelTypeKey.TYPE_USAGE_ARRAY: {
      const dim = (p[TYPE_USAGE_ARRAY.DIMENSION] as number) || 1
      return formatInner(p[TYPE_USAGE_ARRAY.ELEMENT_TYPE]) + '[]'.repeat(dim) + nullable(p)
    }
    case UnifiedGraftModelTypeKey.TYPE_USAGE_GENERIC: {
      const name = p[TYPE_USAGE_GENERIC.GENERIC_NAME] as string
      const args = p.slice(TYPE_USAGE_GENERIC.TYPE_ARGS_FROM).map(formatInner).join(', ')
      return name + (args ? `<${args}>` : '') + nullable(p)
    }
    default:
      return 'unknown'
  }
}

export function formatTypeUsage(typeUsage: NodeLike | null | undefined): string {
  if (!typeUsage || typeof typeUsage !== 'object' || !Array.isArray(typeUsage.payload)) return 'unknown'
  const p = typeUsage.payload
  if (p.length === 0) return 'void'
  const node = resolveNode(typeUsage)
  if (node) return formatNode(node)
  if (typeof p[0] === 'string') return p[0]
  return 'unknown'
}

export function getPrimitiveTypeFromTypeUsage(typeUsage: NodeLike | null | undefined): number | undefined {
  if (!typeUsage?.payload || !Array.isArray(typeUsage.payload)) return undefined
  const node = resolveNode(typeUsage)
  if (!node || getEffectiveTypeUsageKind(node) !== UnifiedGraftModelTypeKey.TYPE_USAGE_PRIMITIVE) return undefined
  const p = node.payload as unknown[]
  if (p.length <= TYPE_USAGE_PRIMITIVE.PRIMITIVE_TYPE) return undefined
  const code = Number(p[TYPE_USAGE_PRIMITIVE.PRIMITIVE_TYPE])
  return Number.isNaN(code) ? undefined : code
}

export function getIsNullableFromTypeUsage(typeUsage: NodeLike | null | undefined): boolean {
  if (!typeUsage?.payload || !Array.isArray(typeUsage.payload)) return false
  const node = resolveNode(typeUsage)

  if (!node) {
    return false
  }

  return isNullable(node.payload as unknown[])
}
